
import { useState } from 'react';
import { Ride } from '@/lib/types/core';
import { Calendar, MapPin, Mountain, ArrowUpDown, User } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";

interface RideListViewProps {
  rides: Ride[];
  loading?: boolean;
}

type SortField = 'date' | 'title' | 'location' | 'difficulty' | 'creator';
type SortDirection = 'asc' | 'desc';

const RideListView = ({ rides, loading = false }: RideListViewProps) => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // If same field, toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // If new field, set it and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const sortedRides = [...rides].sort((a, b) => {
    const directionModifier = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortField) {
      case 'date':
        return a.meeting_date.localeCompare(b.meeting_date) * directionModifier;
      case 'title':
        return a.title.localeCompare(b.title) * directionModifier;
      case 'location':
        // Sort by longitude for simplicity
        return (a.meeting_longitude - b.meeting_longitude) * directionModifier;
      case 'difficulty':
        const difficultyOrder = { 'Easy': 1, 'Moderate': 2, 'Hard': 3 };
        return (difficultyOrder[a.terrain_difficulty as keyof typeof difficultyOrder] - 
                difficultyOrder[b.terrain_difficulty as keyof typeof difficultyOrder]) * directionModifier;
      case 'creator':
        return a.created_by.localeCompare(b.created_by) * directionModifier;
      default:
        return 0;
    }
  });
  
  const SortButton = ({ field, label }: { field: SortField, label: string }) => (
    <button 
      onClick={() => handleSort(field)}
      className="flex items-center hover:text-white transition-colors"
    >
      {label}
      {sortField === field && (
        <ArrowUpDown size={14} className="ml-1 text-adv-orange" />
      )}
    </button>
  );
  
  if (loading) {
    return (
      <div className="glass-card p-6 text-center">
        <p className="text-adv-lightGray">Loading rides...</p>
      </div>
    );
  }
  
  if (rides.length === 0) {
    return (
      <div className="glass-card p-6 text-center">
        <p className="text-adv-lightGray">No rides found. Try adjusting your filters.</p>
      </div>
    );
  }
  
  return (
    <div className="overflow-x-auto glass-card rounded-lg">
      <Table>
        <TableHeader className="bg-adv-charcoal">
          <TableRow>
            <TableHead className="text-adv-lightGray">
              <SortButton field="title" label="Ride Title" />
            </TableHead>
            <TableHead className="text-adv-lightGray">
              <SortButton field="date" label="Date" />
            </TableHead>
            <TableHead className="text-adv-lightGray hidden md:table-cell">
              <SortButton field="location" label="Location" />
            </TableHead>
            <TableHead className="text-adv-lightGray hidden sm:table-cell">
              <SortButton field="difficulty" label="Difficulty" />
            </TableHead>
            <TableHead className="text-adv-lightGray hidden lg:table-cell">
              <SortButton field="creator" label="Organizer" />
            </TableHead>
            <TableHead className="text-right text-adv-lightGray"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRides.map((ride) => (
            <TableRow key={ride.id} className="border-b border-adv-darkGray">
              <TableCell className="font-medium">
                {ride.title}
              </TableCell>
              <TableCell className="text-adv-lightGray whitespace-nowrap">
                <div className="flex items-center">
                  <Calendar size={14} className="mr-2 text-adv-orange" />
                  {new Date(ride.meeting_date).toLocaleDateString()}
                </div>
              </TableCell>
              <TableCell className="text-adv-lightGray hidden md:table-cell">
                <div className="flex items-center">
                  <MapPin size={14} className="mr-2 text-adv-orange" />
                  <span>({ride.meeting_latitude.toFixed(2)}, {ride.meeting_longitude.toFixed(2)})</span>
                </div>
              </TableCell>
              <TableCell className="text-adv-lightGray hidden sm:table-cell">
                <div className="flex items-center">
                  <Mountain size={14} className="mr-2 text-adv-orange" />
                  <span>{ride.terrain_difficulty}</span>
                </div>
              </TableCell>
              <TableCell className="text-adv-lightGray hidden lg:table-cell">
                <div className="flex items-center">
                  <User size={14} className="mr-2 text-adv-orange" />
                  <span>{ride.created_by.username}</span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <NavLink 
                  to={`/ride-hub/${ride.id}`}
                  className="btn-small-primary"
                >
                  View
                </NavLink>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default RideListView;
